import { IonButton, IonItem, IonLabel, IonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { PrivacyBadge } from '../components/PrivacyBadge';
import { CareMapState } from '../caremap/CareMapState';
import dayjs from 'dayjs';
import { AttachmentList } from './generic-notes/attachments/AttachmentList';
import { AddNoteButton } from './add-note/AddNoteButton';
import { ISupportGroupKey } from '../caremap/ICareMap';
import React from 'react';
import { useSubscription } from '@meraki-internal/state';
import { ICaremapNote } from './model/NotesClient';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';

const useStyles = createUseStyles({
    content: {
        display: '-webkit-box',
        overflow: 'hidden !important',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 2,
        // for non-webkit browsers (Firefox)
        maxHeight: '3.8rem',
        // .sc-ion-label-md-s p sets a smaller font-size than we want
        fontSize: 'inherit !important'
    },

    titleWrapper: {
        fontWeight: '500 !important',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        // .sc-ion-label-md-s h3 sets a smaller font-size than we want
        fontSize: 'inherit !important'
    },

    title: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },

    addLink: {
        cursor: 'pointer',
        color: 'var(--ion-color-primary)'
    }
});

type INotesProps = {
    notes?: ICaremapNote[];
    moreNotesHandler?: () => void;
    editNoteHandler: (noteId?: string) => void;
    showCount?: number;
    showHeading?: boolean;
    'data-type'?: string;
    showNoteTargetHeader?: boolean;
};
export const CareMapNotesList: React.FC<INotesProps> = ({
    notes,
    moreNotesHandler,
    editNoteHandler,
    showCount,
    showHeading = true,
    'data-type': dataType = 'notes-list',
    showNoteTargetHeader
}) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);

    // intentionanlly not subscribing b/c this cannot change
    const canEdit = careMapState.getActiveCaremap()!.links.self.actions.includes('edit-notes');

    const formatDate = (date: string) => {
        const dayjsDate = dayjs(date);
        const format = dayjsDate.isToday() ? 'h:mma' : 'MM/DD/YY';
        return dayjsDate.format(format);
    };

    notes = (notes || []).sort((n1, n2) => dayjs(n1.updatedAt).isAfter(n2.updatedAt) ? -1 : 1);

    const visibleNotes: ICaremapNote[] = notes.slice(0, showCount);

    const isAnytingToSee = canEdit || visibleNotes.length > 0;
    if(!isAnytingToSee){
        return (
            <div data-type="viewer-no-notes">
                No public notes yet… Check back soon!
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }} data-type={dataType}>
            {showHeading && (
                <DrawerSectionHeading text="Notes">
                    <AddNoteButton
                        canAdd={canEdit}
                        onClick={editNoteHandler}
                    />
                </DrawerSectionHeading>
            )}

            {visibleNotes.length === 0 && canEdit && (
                <IonText color="medium" style={{paddingLeft: 12}}>
                    No notes yet, <span className={classes.addLink} onClick={() => editNoteHandler('add-note')}>add one</span>
                </IonText>
            )}

            {(canEdit || visibleNotes.length > 0) && visibleNotes.map(note => (
                <IonItem key={note.noteId}
                    button
                    detail
                    data-type="note-item"
                    data-node-id={note.noteId}
                    style={{'--border-radius': '8px'}}
                    onClick={() => editNoteHandler(note.noteId)}>
                    <IonLabel class="ion-text-wrap">
                        <h3 className={classes.titleWrapper}>
                            <div className={classes.title}>
                                <span style={{color: 'var(--ion-color-step-600, #666666)'}}>
                                    {formatDate(note.updatedAt!)}
                                    {showNoteTargetHeader && (
                                        <NoteTargetHeader note={note}/>
                                    )}
                                </span>
                                &nbsp;
                                {note.title && <span data-type="title">{note.title}</span>}
                            </div>
                            <div>
                                {note.isPrivate ? <PrivacyBadge /> : null}
                            </div>
                        </h3>
                        <p className={classes.content}
                            data-type="content"
                            dangerouslySetInnerHTML={{ __html: note.html.split('\n').join('<br/>')}}
                        />
                        <AttachmentList
                            note={note}
                            attachments={note.attachments}
                        />
                    </IonLabel>
                </IonItem>
            ))}

            {(showCount && showCount < notes.length && moreNotesHandler) && (
                <div style={{display: 'flex', justifyContent: 'space-around', marginBottom: -24}}>
                    <IonButton fill="clear" onClick={moreNotesHandler} data-id="view-more-notes-button">View More Notes</IonButton>
                </div>
            )}
        </div>
    );
};

const NoteTargetHeader: React.FC<{ note: ICaremapNote }> = ({ note }) => {
    const careMapState = useInstance(CareMapState);
    const activeCaremap = careMapState.getActiveCaremap();

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    if(!activeCaremap){
        return null;
    }

    const noteTarget = note.otherAttributes.noteTarget;

    if (!noteTarget){
        return null;
    }

    if (noteTarget.section === 'patient'){
        return null;
    }

    let children: React.ReactNode;

    if (!noteTarget.memberId){
        const sectionTitle = (careMapState.getAllSupportGroupLabels() as any)[noteTarget.section]?.title || noteTarget.section;
        children = <span>{sectionTitle}</span>;
    }
    else if (noteTarget.section === 'household'){
        const member = careMapState.getHouseholdMember(noteTarget.memberId);
        children = <MemberName {...member} />;
    }
    else {
        const member = careMapState.getSupportGroupMember({ supportGroupKey: noteTarget.section as ISupportGroupKey, memberId: noteTarget.memberId });
        if (!member || (!member.role && !member.name)){
            return null;
        }
        children = (<span>{member.role || member.name}</span>);
    }

    return (
        <span>
            <span style={{ marginLeft: 3, marginRight: 3}}>|</span>
            {children}
            {note.title && (
                <span> |</span>
            )}
        </span>
    );
};

const MemberName: React.FC<{ firstName?: string; lastName?: string; }> = ({ firstName, lastName }) => {
    let name = '';
    if (firstName){
        name = firstName;
    }
    if (name && lastName){
        name  = name + ' ';
    }
    if (lastName){
        name = name + lastName;
    }

    return name;
};

